import TrashIcon from '@/shared/assets/icons/trash.svg?react'
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { X } from 'lucide-react'
import React, { Fragment, ReactElement } from 'react'
import { BaseButton } from '../BaseButton/BaseButton'
import { ModalButtons } from '../ModalButtons/ModalButtons'

interface BaseDialogProps {
  children: React.ReactNode
  title?: string
  iconTitle?: ReactElement
  closeButtonName?: string
  actionButtonName?: string
  isVisible: boolean
  isLoading?: boolean
  onClose: () => void
  onAction?: () => void
  onDelete?: () => void
  onStepBack?: () => void
  closeButton?: boolean
  closeButtonVariant?: 'primary' | 'icon' | 'redText' | 'purple'
  actionButtonVariant?: 'purple' | 'red'
  buttonDirection?: 'row' | 'column'
  xButton?: boolean
}

export const BaseDialog = ({
  children,
  title,
  closeButtonName,
  actionButtonName,
  isVisible,
  onClose,
  onAction,
  onDelete,
  closeButton = false,
  xButton = true,
  isLoading = false,
  iconTitle,
  onStepBack,
  actionButtonVariant,
  closeButtonVariant,
  buttonDirection,
}: BaseDialogProps) => {
  return (
    <Transition show={isVisible} as={Fragment}>
      <Dialog as="div" className="z-50 relative" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-dark-bg/50 dark:bg-dark-bg/80" />
        </TransitionChild>

        <div className="fixed inset-0 flex justify-center items-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="flex flex-col gap-y-6 bg-light-bg dark:bg-dark-bg px-6 py-6 md:py-8 dark:ring-card-border-dark rounded-3xl md:rounded-2xl dark:ring w-full max-w-[465px] max-h-[90vh] overflow-x-auto transition-all scrollbar-hide transform">
              <div className="flex justify-between items-start">
                {title && (
                  <DialogTitle
                    as="h3"
                    className="font-medium text-main-black dark:text-white text-xl leading-6"
                  >
                    {title}
                  </DialogTitle>
                )}
                {iconTitle && iconTitle}
                {xButton && (
                  <button
                    onClick={onClose}
                    className="hover:opacity-80 text-main-black dark:text-white cursor-pointer"
                  >
                    <X width={24} height={24} />
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-y-6 text-sm md:text-base">{children}</div>
              {!onAction && !closeButton ? null : (
                <>
                  <ModalButtons
                    isLoadActionButton={isLoading}
                    actionButton={!!onAction}
                    actionButtonName={actionButtonName}
                    actionButtonVariant={actionButtonVariant}
                    closeButton={closeButton}
                    closeButtonName={closeButtonName}
                    closeButtonVariant={closeButtonVariant}
                    direction={buttonDirection}
                    onAction={onAction}
                    onClose={onClose}
                    onStepBack={onStepBack}
                  />
                  {onDelete && (
                    <BaseButton
                      variant="ghost"
                      padding="none"
                      onClick={onDelete}
                      iconPosition="right"
                      className="aria-disabled:bg-transparent disabled:bg-transparent dark:aria-disabled:bg-transparent dark:disabled:bg-transparent !py-3 ring-card-border hover:bg-card-border focus:ring-card-border dark:hover:bg-card-border-dark dark:focus:card-border-dark dark:ring-card-border-dark ring-1 focus:ring-2 ring-inset"
                      icon={<TrashIcon className="text-error" />}
                    >
                      Удалить реквизит
                    </BaseButton>
                  )}
                </>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
